import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../layouts/Modal/Modal';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import styles from './TrackingErrorModal.module.scss';

export type TrackingErrorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  errorDetail: string;
};

const TrackingErrorModal: React.FC<TrackingErrorModalProps> = ({ isOpen, onClose, errorDetail }) => {
  const { t } = useTranslation();
  const [helpContentOpen, setHelpContentOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const helpContent = [
    {
      id: 1,
      title: t('tracking.help_item_title_1'),
      description: t('tracking.help_item_description_1'),
    },
    {
      id: 2,
      title: t('tracking.help_item_title_2'),
      description: t('tracking.help_item_description_2'),
    },
    {
      id: 3,
      title: t('tracking.help_item_title_3'),
      description: t('tracking.help_item_description_3'),
    },
  ];

  const handleHelpToggle = (showHelp: boolean) => {
    setIsAnimating(true);
    setHelpContentOpen(showHelp);
    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setHelpContentOpen(false);
      }}>
      <div className="max-w-[480px] max-h-[570px]">
        <div className="px-24 md:px-32 py-16 md:py-22 border-subtle">
          <div
            className={`flex items-center ${!helpContentOpen ? 'w-full justify-center' : 'w-[55%] justify-between'}`}>
            {helpContentOpen && (
              <div className="flex items-center justify-self-start gap-4">
                <Button
                  className="typo-text-16--bold text-faded !p-0"
                  title={t('inbound.back')}
                  tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT}
                  icon="arrowLeft"
                  onClick={() => handleHelpToggle(false)}
                />
              </div>
            )}
            <span className="typo-text-16--bold"> {t('navbar.help')}</span>
          </div>
        </div>
        <div className="relative overflow-hidden">
          <div className={`${styles.contentContainer} ${helpContentOpen && styles.helpActive}`}>
            <div className={`${styles.view} ${styles.errorView} ${isAnimating && styles.animating}`}>
              <div className="p-24 md:p-32">
                <div className="flex flex-col gap-[12px] justify-center items-center">
                  <img src="/assets/illustrations/error-picture.svg" alt="error" className="w-[16rem] h-[16rem]" />
                  <h4 className="mb-12 typo-title-36">{t('tracking.error_modal_title')}</h4>
                  <p className="typo-text-16 text-faded text-center">{errorDetail}</p>
                  <span
                    onClick={() => handleHelpToggle(true)}
                    role="none"
                    className="typo-text-16 text-faded underline cursor-pointer">
                    {t('general.help')}
                  </span>
                  <Button
                    className="w-[150px] !max-h-none h-[56px] mt-24 md:mt-32"
                    onClick={onClose}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    title={t('general.try_again')}
                  />
                </div>
              </div>
            </div>

            <div className={`${styles.view} ${styles.helpView} ${isAnimating && styles.animating}`}>
              <div className="p-24 md:p-32">
                <div className="flex flex-col gap-[32px] text-start">
                  {helpContent.map(item => (
                    <div key={item.id} className="flex flex-col gap-[16px]">
                      <span className="typo-text-16--bold text-faded">{item.title}</span>
                      <p className="typo-text-16 text-faded">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TrackingErrorModal;
